import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCourse } from "../../../../redux/actions/contentCoursesActions";
import DraggableList from "../../../../components/DraggableList/DraggableList";
import ShortCourse from "../../../../components/CourseBuilder/ShortCourse";
import { reorder, setItemsPosition } from "../../../../utils/tableUtils";
import { sortBy } from "lodash";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { courseSectionTypes } from "../../../../constants";

const selector = makeGetEntities();

const CourseBuilderCourseSections = ({
  isDraggingIcon,
  onDropItem,
  onEditActivity,
  versionId,
  sectionType
}) => {
  const [courseSections, setCourseSections] = useState([]);
  const dispatch = useDispatch();
  const { versionData, isAddingCourseSection, isUpdatingCourse } = useSelector(
    ({ contentCoursesState }) => ({
      versionData: selector(contentCoursesState, {
        type: "version",
        id: versionId
      }),
      isAddingCourseSection: contentCoursesState.isAddingCourse,
      isUpdatingCourse: contentCoursesState.isUpdatingCourse
    })
  );

  /**
   * Filters the list of course sections by the current section type ("mandatory" for
   * table of contents and "optional for resources") and sorts them by their position.
   * If isUpdatingCourse is true, that means the request to reorder the course sections
   * is in progress, so we don't want to update courseSections until that request is complete
   */
  useEffect(() => {
    if (versionData && versionData.courseSections && !isUpdatingCourse) {
      const courseSections = sortBy(
        versionData.courseSections.filter(
          courseSection => courseSection.sectionType === sectionType
        ),
        ["position"]
      );
      setCourseSections([...courseSections]);
    }
  }, [versionData, sectionType]);

  useEffect(() => {
    if (versionData && versionData.courseSections && !isUpdatingCourse) {
      const courseSections = sortBy(
        versionData.courseSections.filter(
          courseSection =>
            courseSection.sectionType === sectionType &&
            !courseSection.isOverview
        ),
        ["position"]
      );
      setCourseSections([...courseSections]);
    }
  }, [versionData, sectionType]);

  const handleReorderShortCourses = reorderedSections => {
    // dropped outside the list
    if (!reorderedSections.destination) {
      return;
    }
    if (
      reorderedSections.source.index === reorderedSections.destination.index
    ) {
      return;
    }
    let reordered = reorder(
      courseSections,
      reorderedSections.source.index,
      reorderedSections.destination.index
    );
    reordered = setItemsPosition(reordered, "position");
    setCourseSections(reordered);
    dispatch(
      updateCourse(versionId, {
        ...versionData,
        courseSections: reordered
      })
    );
  };

  const overviewCourseSection =
    versionData && versionData.courseSections
      ? versionData.courseSections.find(({ isOverview }) => isOverview)
      : null;

  return (
    <>
      {versionData &&
        versionData.showOverview &&
        overviewCourseSection &&
        sectionType === courseSectionTypes.mandatory && (
          <ShortCourse
            key={overviewCourseSection.id}
            courseSection={overviewCourseSection}
            versionId={versionId}
          />
        )}
      <DraggableList
        onDragEnd={handleReorderShortCourses}
        droppableId="short-courses"
        items={courseSections
          .filter(courseSection => {
            return !courseSection.isOverview;
          })
          .map((courseSection, index) => ({
            render: ({ isDragging, dragHandle, isDraggingOver }) => (
              <ShortCourse
                courseSection={courseSection}
                isDraggingIcon={isDraggingIcon}
                onDropItem={onDropItem}
                versionId={versionId}
                isDraggingSection={isDragging}
                dragHandle={dragHandle}
                isDraggingOver={isDraggingOver}
                onEditActivity={onEditActivity}
              />
            ),
            draggableId: courseSection.id
          }))}
      />
      {/* While a course section is being added, we'll show a temporary short course with a loading spinner*/}
      {isAddingCourseSection && <ShortCourse showLoadingSpinner={true} />}
    </>
  );
};

export default CourseBuilderCourseSections;

CourseBuilderCourseSections.propTypes = {
  /**
   * True if the user is dragging an Activity Type icon. This is used to
   * highlight the boxes in the short courses when they are dragging an icon
   */
  isDraggingIcon: PropTypes.bool,
  /**
   * The ID for the course version that the course sections belong to
   */
  versionId: PropTypes.string,
  /**
   * Called when the user drops an activity type icon into the Course
   * Section drop box
   */
  onDropItem: PropTypes.func,
  sectionType: PropTypes.oneOf(["mandatory", "optional"])
};
